import { z } from "zod";

import { CancelReason, CancelReasonType } from "./types";

export const CANCEL_REASON_TYPES = {
  INTERNAL: "internal",
  EXTERNAL: "external",
} as const satisfies Record<string, CancelReasonType>;

export const CANCELLATION_NOTES_MAX_LENGTH = 1000;

/**
 * The fields the admin actually edits, shared by the cancel and the edit modals. The
 * appointment and patient ids are context rather than input, so they are not modelled here:
 * the cancel flow attaches them when it builds its request.
 */
export const cancellationReasonSchema = z.object({
  cancelReasonParentId: z.string().min(1, "Please select a reason"),
  cancelReasonChildId: z.string().optional(),
  cancellationNotes: z
    .string()
    .max(
      CANCELLATION_NOTES_MAX_LENGTH,
      `Notes cannot exceed ${CANCELLATION_NOTES_MAX_LENGTH} characters`,
    )
    .optional(),
  waiveFee: z.boolean().optional(),
});

/**
 * Whether a sub-reason is required depends on the parent the admin picked, so the rule
 * needs the fetched list. Build the schema once the reasons are in and memoise it.
 */
export const makeCancellationReasonSchema = (reasons: CancelReason[] = []) =>
  cancellationReasonSchema.superRefine((values, ctx) => {
    const parent = reasons.find(
      (reason) => reason.id.toString() === values.cancelReasonParentId,
    );

    if (parent?.children.length && !values.cancelReasonChildId) {
      ctx.addIssue({
        code: "custom",
        path: ["cancelReasonChildId"],
        message: "Please select a sub-reason",
      });
    }
  });

export const cancelVisitSchema = cancellationReasonSchema.extend({
  appointmentId: z.string(),
  patientId: z.string(),
});

/**
 * Deliberately narrower than the payload. The endpoint also returns the saved cancellation,
 * but the modal only branches on the sync flag and then loads the history again, so modelling
 * the rest would buy nothing and cost something: a renamed transformer field would fail
 * .parse() and surface as an error on an edit that actually succeeded.
 */
export const editVisitCancellationResponseSchema = z.object({
  athena_sync_failed: z.boolean(),
});

export const cancelReasonTypeSchema = z.enum(["internal", "external"]);

const cancelReasonBaseSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: cancelReasonTypeSchema,
  parentId: z.number().nullable(),
  bearsLateFee: z.boolean(),
  allowsFreeText: z.boolean(),
});

/**
 * Two explicit levels rather than a recursive schema: the API eager-loads one level of
 * children, so promising more depth than that would be a lie.
 */
export const cancelReasonSchema = cancelReasonBaseSchema.extend({
  children: z.array(cancelReasonBaseSchema),
});

export const bookingProviderSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    title: z.string(),
    image_url: z.string().nullable(),
    in_network: z.boolean(),
    is_most_recent: z.boolean(),
    next_available_at: z.string().nullable(),
    available_today: z.boolean(),
  })
  .transform((provider) => ({
    id: provider.id,
    name: provider.name,
    title: provider.title,
    imageUrl: provider.image_url,
    inNetwork: provider.in_network,
    isMostRecent: provider.is_most_recent,
    nextAvailableAt: provider.next_available_at,
    availableToday: provider.available_today,
  }));

export const bookingProvidersResponseSchema = z.object({
  data: z.array(bookingProviderSchema),
});
